import React, { isValidElement, forwardRef, memo } from 'react';
import classnames from 'classnames';
import T from 'prop-types';
import { Popup } from 'semantic-ui-react';

import styles from './styles.scss';
import { FONT_FAMILIES, THEMES, SIZES, HEIGHTS, TITLE_POSITION } from './constants';

const ButtonIcon = ({ icon, iconClassName }) => {
  const isElement = isValidElement(icon);

  return (
    <div
      className={classnames(styles.icon, iconClassName)}
      {...(!isElement && typeof icon === 'string'
        ? { dangerouslySetInnerHTML: { __html: icon } }
        : {})}
    >
      {isElement ? icon : null}
    </div>
  );
};

export const ButtonPopup = ({
  children,
  position = TITLE_POSITION.TOP_CENTER,
  mouseEnterDelay = 1000,
  on = 'hover',
  content = '',
  ...popupProps
}) => {
  return (
    <Popup
      position={position}
      mouseEnterDelay={mouseEnterDelay}
      on={on}
      {...popupProps}
      trigger={children}
      content={content}
    />
  );
};

const ButtonPure = forwardRef(({
  className,
  circle = false,
  children,
  contentClassName,
  disabled,
  fontFamily = FONT_FAMILIES.REGULAR,
  height,
  icon,
  iconClassName,
  loading = false,
  size = SIZES.MEDIUM,
  theme = THEMES.CRM,
  onClick,
  type = 'button',
  fluid,
  ...buttonProps
}, ref) => {
  return (
    <button
      className={classnames(
        styles.root,
        className,
        styles[`theme--${theme}`],
        styles[`size--${size}`],
        styles[`fontFamily--${fontFamily}`],
        {
          [styles.disabled]: disabled,
          [styles.circle]: circle,
          [styles.loading]: loading,
          [styles.onlyIcon]: !children,
          [styles.fluid]: fluid,
          [styles[`height--${height}`]]: !!height,
        }
      )}
      type={type}
      ref={ref}
      onClick={!disabled ? onClick : undefined}
      {...buttonProps}
    >
      { !loading && (
        <>
          {icon && (
            <ButtonIcon
              icon={icon}
              iconClassName={iconClassName}
            />
          )}
          {Boolean(children) && (
            <span className={classnames(styles.content, contentClassName)}>
              { children }
            </span>
          )}
        </>
      ) }
    </button>
  );
});

ButtonPure.propTypes = {
  className: T.string,
  circle: T.bool,
  children: T.any,
  contentClassName: T.string,
  disabled: T.bool,
  fontFamily: T.oneOf(Object.values(FONT_FAMILIES)),
  height: T.oneOf(Object.values(HEIGHTS)),
  icon: T.any,
  iconClassName: T.string,
  loading: T.bool,
  fluid: T.bool,
  type: T.string,
  size: T.oneOf(Object.values(SIZES)),
  theme: T.oneOf(Object.values(THEMES)),
  onClick: T.func,
};

export const Button = memo(ButtonPure);

Button.ButtonPopup = ButtonPopup;
